import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../index';

interface StoreState {
  selectedStoreId: string | null;
  selectedOutletId: string | null;
  selectedOutletDrawerId: string | null;
  selectedOutletDrawerName: string | null;
  storeName: string | null;
  outletName: string | null;
}

// Get initial state from localStorage
const getInitialState = (): StoreState => {
  try {
    const savedStoreId = localStorage.getItem('selectedStoreId');
    const savedOutletId = localStorage.getItem('selectedOutletId');
    const savedOutletDrawerId = localStorage.getItem('selectedOutletDrawerId');
    const savedOutletDrawerName = localStorage.getItem('selectedOutletDrawerName');
    const savedStoreName = localStorage.getItem('selectedStoreName');
    const savedOutletName = localStorage.getItem('selectedOutletName');

    return {
      selectedStoreId: savedStoreId,
      selectedOutletId: savedOutletId,
      selectedOutletDrawerId: savedOutletDrawerId, // Load from local storage
      selectedOutletDrawerName: savedOutletDrawerName,
      storeName: savedStoreName,
      outletName: savedOutletName,
    };
  } catch (error) {
    console.error('Error loading store selection from localStorage:', error);
    return {
      selectedStoreId: null,
      selectedOutletId: null,
      selectedOutletDrawerId: null,
      selectedOutletDrawerName: null,
      storeName: null,
      outletName: null,
    };
  }
};

export const storeSlice = createSlice({
  name: 'store',
  initialState: getInitialState(),
  reducers: {
    setSelectedStore: (
      state,
      action: PayloadAction<{ storeId: string; storeName: string }>
    ) => {
      state.selectedStoreId = action.payload.storeId;
      state.storeName = action.payload.storeName;

      // Persist to localStorage
      localStorage.setItem('selectedStoreId', action.payload.storeId);
      localStorage.setItem('selectedStoreName', action.payload.storeName);
    },

    setSelectedOutlet: (
      state,
      action: PayloadAction<{ outletId: string; outletName: string }>
    ) => {
      state.selectedOutletId = action.payload.outletId;
      state.outletName = action.payload.outletName;

      // Persist to localStorage
      localStorage.setItem('selectedOutletId', action.payload.outletId);
      localStorage.setItem('selectedOutletName', action.payload.outletName);
    },

    setSelectedOutletDrawer: (state, action: PayloadAction<{ id: string; name: string } | null>) => {
      if (action.payload) {
        state.selectedOutletDrawerId = action.payload.id;
        state.selectedOutletDrawerName = action.payload.name;
        localStorage.setItem('selectedOutletDrawerId', action.payload.id);
        localStorage.setItem('selectedOutletDrawerName', action.payload.name);
      } else {
        state.selectedOutletDrawerId = null;
        state.selectedOutletDrawerName = null;
        localStorage.removeItem('selectedOutletDrawerId');
        localStorage.removeItem('selectedOutletDrawerName');
      }
    },

    setStoreAndOutlet: (
      state,
      action: PayloadAction<{
        storeId: string;
        storeName: string;
        outletId: string;
        outletName: string;
      }>
    ) => {
      state.selectedStoreId = action.payload.storeId;
      state.storeName = action.payload.storeName;
      state.selectedOutletId = action.payload.outletId;
      state.outletName = action.payload.outletName;

      // Persist to localStorage
      localStorage.setItem('selectedStoreId', action.payload.storeId);
      localStorage.setItem('selectedStoreName', action.payload.storeName);
      localStorage.setItem('selectedOutletId', action.payload.outletId);
      localStorage.setItem('selectedOutletName', action.payload.outletName);
    },

    clearSelection: (state) => {
      state.selectedStoreId = null;
      state.selectedOutletId = null;
      state.selectedOutletDrawerId = null;
      state.selectedOutletDrawerName = null;
      state.storeName = null;
      state.outletName = null;

      // Clear from localStorage
      localStorage.removeItem('selectedStoreId');
      localStorage.removeItem('selectedStoreName');
      localStorage.removeItem('selectedOutletId');
      localStorage.removeItem('selectedOutletName');
      localStorage.removeItem('selectedOutletDrawerId');
      localStorage.removeItem('selectedOutletDrawerName');
    },
  },
  extraReducers: (builder) => {
    // Sync with auth actions
    // Note: We need to use string action types to avoid circular dependencies if imports cause issues
    // But since authSlice doesn't import storeSlice, we can try importing.
    // If build fails, we'll use string literals 'auth/login/fulfilled', 'auth/verifyToken/fulfilled', etc.

    builder.addMatcher(
      (action) => action.type === 'auth/logout/fulfilled' || action.type === 'auth/clearAuth',
      (state) => {
        state.selectedStoreId = null;
        state.selectedOutletId = null;
        state.selectedOutletDrawerId = null;
        state.selectedOutletDrawerName = null;
        state.storeName = null;
        state.outletName = null;
        localStorage.removeItem('selectedStoreId');
        localStorage.removeItem('selectedStoreName');
        localStorage.removeItem('selectedOutletId');
        localStorage.removeItem('selectedOutletName');
        localStorage.removeItem('selectedOutletDrawerId');
        localStorage.removeItem('selectedOutletDrawerName');
      }
    );

    builder.addMatcher(
      (action) => action.type === 'auth/verifyToken/fulfilled',
      (state, action: PayloadAction<any>) => {
        const user = action.payload;
        if (user?.currentStore) {
          state.selectedStoreId = user.currentStore.id;
          state.storeName = user.currentStore.name;
          localStorage.setItem('selectedStoreId', user.currentStore.id);
          localStorage.setItem('selectedStoreName', user.currentStore.name);
        } else {
          // If verification succeeded but no currentStore (e.g. user has no stores), clear selection
          // But wait, verifyToken might have restored from localStorage which we just cleared in Stores.tsx?
          // If we cleared localStorage, verifyToken logic might pick default store.
          // If it picked default, user.currentStore will be set.
          // If not set, we should clear.
          if (!user?.currentStore) {
            state.selectedStoreId = null;
            state.storeName = null;
            localStorage.removeItem('selectedStoreId');
            localStorage.removeItem('selectedStoreName');
          }
        }

        if (user?.currentOutlet) {
          state.selectedOutletId = user.currentOutlet.id;
          state.outletName = user.currentOutlet.name;
          localStorage.setItem('selectedOutletId', user.currentOutlet.id);
          localStorage.setItem('selectedOutletName', user.currentOutlet.name);
        } else if (!user?.currentOutlet) {
          state.selectedOutletId = null;
          state.outletName = null;
          localStorage.removeItem('selectedOutletId');
          localStorage.removeItem('selectedOutletName');
        }
      }
    );

    builder.addMatcher(
      (action) => action.type === 'auth/login/fulfilled',
      (state, action: PayloadAction<any>) => {
        // login payload: { user, token, ... }
        const user = action.payload.user;
        if (user?.currentStore) {
          state.selectedStoreId = user.currentStore.id;
          state.storeName = user.currentStore.name;
          localStorage.setItem('selectedStoreId', user.currentStore.id);
          localStorage.setItem('selectedStoreName', user.currentStore.name);
        }
        if (user?.currentOutlet) {
          state.selectedOutletId = user.currentOutlet.id;
          state.outletName = user.currentOutlet.name;
          localStorage.setItem('selectedOutletId', user.currentOutlet.id);
          localStorage.setItem('selectedOutletName', user.currentOutlet.name);
        }
      }
    );

    // Also handle switchStore/switchOutlet fulfilled
    builder.addMatcher(
      (action) => action.type === 'auth/switchStore/fulfilled',
      (state, action: PayloadAction<any>) => {
        // payload: { currentStore, currentOutlet, ... }
        const { currentStore, currentOutlet } = action.payload;
        if (currentStore) {
          state.selectedStoreId = currentStore.id;
          state.storeName = currentStore.name;
          localStorage.setItem('selectedStoreId', currentStore.id);
          localStorage.setItem('selectedStoreName', currentStore.name);
        }
        if (currentOutlet) {
          state.selectedOutletId = currentOutlet.id;
          state.outletName = currentOutlet.name;
          localStorage.setItem('selectedOutletId', currentOutlet.id);
          localStorage.setItem('selectedOutletName', currentOutlet.name);
        } else {
          // IMPORTANT: Clear outlet when switching to store with no outlets
          state.selectedOutletId = null;
          state.outletName = null;
          localStorage.removeItem('selectedOutletId');
          localStorage.removeItem('selectedOutletName');
        }
        // Always clear outlet drawer when switching stores - drawer is outlet-specific
        state.selectedOutletDrawerId = null;
        state.selectedOutletDrawerName = null;
        localStorage.removeItem('selectedOutletDrawerId');
        localStorage.removeItem('selectedOutletDrawerName');
      }
    );

    builder.addMatcher(
      (action) => action.type === 'auth/switchOutlet/fulfilled',
      (state, action: PayloadAction<any>) => {
        const { currentOutlet } = action.payload;
        if (currentOutlet) {
          state.selectedOutletId = currentOutlet.id;
          state.outletName = currentOutlet.name;
          localStorage.setItem('selectedOutletId', currentOutlet.id);
          localStorage.setItem('selectedOutletName', currentOutlet.name);
        }
      }
    );
  },
});

export const {
  setSelectedStore,
  setSelectedOutlet,
  setSelectedOutletDrawer,
  setStoreAndOutlet,
  clearSelection,
} = storeSlice.actions;

// Base selector
const selectStoreState = (state: RootState) => state.store;

// Memoized Selectors
export const selectCurrentStore = createSelector(
  [selectStoreState],
  (store) => ({
    storeId: store.selectedStoreId,
    storeName: store.storeName,
  })
);

export const selectCurrentOutlet = createSelector(
  [selectStoreState],
  (store) => ({
    outletId: store.selectedOutletId,
    outletName: store.outletName,
  })
);

export const selectCurrentOutletDrawer = createSelector(
  [selectStoreState],
  (store) => ({
    outletDrawerId: store.selectedOutletDrawerId,
    outletDrawerName: store.selectedOutletDrawerName,
  })
);

export const selectStoreAndOutlet = createSelector(
  [selectStoreState],
  (store) => ({
    storeId: store.selectedStoreId,
    storeName: store.storeName,
    outletId: store.selectedOutletId,
    outletName: store.outletName,
    outletDrawerId: store.selectedOutletDrawerId,
    outletDrawerName: store.selectedOutletDrawerName,
  })
);

export default storeSlice.reducer;
